import apiClient from '../client';
import type {
  PaginatedResponse,
  TenderAPIV2,
  TenderListItemV2,
  TenderSkillAPIV2,
  TenderStatusV2,
} from '../../types';

// NOTE: `baseURL` override is a temporary workaround until the apiClient is
// refactored to use `/api` as base and embed the version in the path.
// Track the cleanup separately; do not extend this pattern elsewhere.
const V2_BASE = '/api/v2';

// The v2 controller wraps every response in APIEnvelopDTO { status, error,
// payload }. The list payload is a Spring Page (snake_case fields, matching the
// app-wide Jackson naming strategy).
interface APIEnvelope<T> {
  status: number;
  error: string | null;
  payload: T;
}

interface SpringPage<T> {
  content: T[];
  number: number; // 0-based
  size: number;
  total_elements: number;
  total_pages: number;
}

export interface TenderListParamsV2 {
  page?: number; // 1-based; mapped to Spring's 0-based `page` here
  pageSize?: number;
  status?: TenderStatusV2 | '';
  // Spring sort expression, e.g. "receivedAt,desc". Sortable properties are
  // TenderV2 fields: title, receivedAt, status. Defaults server-side to
  // receivedAt,desc when omitted.
  sort?: string;
}

/**
 * Fetch a paginated list of tenders from the v2 endpoint (reads `tenders`).
 *
 * The endpoint has no free-text search, so it is not accepted here. Unwraps the
 * APIEnvelopDTO and maps the Spring Page to the app's `PaginatedResponse` shape
 * (1-based page).
 */
export async function fetchTendersV2(
  params: TenderListParamsV2 = {},
): Promise<PaginatedResponse<TenderListItemV2>> {
  const springParams: Record<string, unknown> = {
    page: (params.page ?? 1) - 1,
    size: params.pageSize ?? 20,
    ...(params.status ? { status: params.status } : {}),
    ...(params.sort ? { sort: params.sort } : {}),
  };

  const { data } = await apiClient.get<APIEnvelope<SpringPage<TenderListItemV2>>>(
    '/tenders',
    { baseURL: V2_BASE, params: springParams },
  );

  const page = data.payload;
  return {
    items: page?.content ?? [],
    total: page?.total_elements ?? 0,
    page: (page?.number ?? 0) + 1,
    page_size: page?.size ?? 20,
    total_pages: page?.total_pages ?? 1,
  };
}

/**
 * Fetch a tender by ID.
 * Returns `null` if the backend responds 404 (tender does not exist).
 * Any other non-2xx response is thrown by the apiClient interceptor.
 */
export async function fetchTenderV2(tenderId: string): Promise<TenderAPIV2 | null> {
  const response = await apiClient.get<TenderAPIV2>(`/tenders/${tenderId}`, {
    baseURL: V2_BASE,
    validateStatus: (status) => status === 200 || status === 404,
  });
  if (response.status === 404) return null;
  return response.data;
}

/**
 * Re-extract a tender: the backend purges the extracted stage outputs
 * (sections, triage, extracted criteria, their skills) and re-runs the
 * pipeline from the original raw content. Recruiter-added criteria and their
 * skills are preserved; candidate matching is left untouched.
 *
 * The pipeline runs asynchronously — the backend returns 202 immediately and
 * the new results land minutes later. Resolves to `true` when accepted (202),
 * `false` when the tender does not exist (404).
 */
export async function reextractTenderV2(tenderId: string): Promise<boolean> {
  const response = await apiClient.post<void>(`/tenders/${tenderId}/reextract`, null, {
    baseURL: V2_BASE,
    validateStatus: (status) => status === 202 || status === 404,
  });
  return response.status === 202;
}

/**
 * Remove (`active=false`, with a `reason`) or add back (`active=true`) a
 * taxonomy-resolved skill on the tender overview. `reason` is the deactivation
 * controlled vocabulary (`not_required` / `not_a_skill` / `not_present`) and is
 * only sent on a removal. Returns the updated skill, or `null` if the tender has
 * no such skill (404).
 */
export async function setTenderSkillActiveV2(
  tenderId: string,
  skillId: string,
  active: boolean,
  reason?: string,
): Promise<TenderSkillAPIV2 | null> {
  const response = await apiClient.patch<TenderSkillAPIV2>(
    `/tenders/${tenderId}/skills/${skillId}`,
    active ? { active } : { active, reason },
    {
      baseURL: V2_BASE,
      validateStatus: (status) => status === 200 || status === 404,
    },
  );
  if (response.status === 404) return null;
  return response.data;
}
